import {IDocumentVerificationValueSource, IFaceMatchSource, ValidationDocCountryCode, ValidationDocType, ValidationGroupType, ValidationType} from './types';
import {isValidationGroupType, IValidationGroup} from './validation-group.schema';

/* --------------  Base Validation -------------- */
export type IValidation = {
	ordinal: number;
	type: ValidationType;
};

export function isValidation(validation: IValidation): validation is IValidation {
	return validation.hasOwnProperty('type');
}

/* ---------- Regex Validation ---------- */
export type IRegexValidation = IValidation & {
	type: typeof ValidationType.Regex;
	/*	@description The key of the regex pattern to use */
	key: string;
};

function tryConvertValidationGroupString(validationString: unknown): IValidationGroup | null {
	if (typeof validationString === 'string') {
		try {
			const validation: IValidation = JSON.parse(validationString) as IValidation;
			if (isValidation(validation)) return null;
			if (isValidationGroupType(validation)) return validation as IValidationGroup;
		} catch (error) {
			// is string with validation key only, convert to regex validation:
			const regexValidation: IRegexValidation = {
				ordinal: 1,
				type: ValidationType.Regex,
				key: validationString,
			};
			const group: IValidationGroup = {
				groupType: ValidationGroupType.Regex,
				validations: [regexValidation],
			};
			return group;
		}
	}
	return null;
}

export function validateRegexValidation(validationString: unknown): IRegexValidation | null {
	const parsedValidationGroup = tryConvertValidationGroupString(validationString);
	if (parsedValidationGroup) {
		const regexValidation = parsedValidationGroup.validations.find(isRegex);
		if (regexValidation) return regexValidation;
	}
	return null;
}

export function isRegex(validation: IValidation): validation is IRegexValidation {
	return validation.type === ValidationType.Regex;
}

/* ---------- Liveness Validation ---------- */
export type ILivenessValidation = IValidation & {
	type: typeof ValidationType.Liveness;
	passive?: boolean | undefined;
};

export function isLiveness(validation: IValidation): validation is ILivenessValidation {
	return validation.type === ValidationType.Liveness;
}

/* ---------- FaceMatch Validation ---------- */
export type IFaceMatchValidation = IValidation & {
	type: typeof ValidationType.FaceMatch;
	imageToCompare: IFaceMatchSource;
	imageData?: string | null;
};

export function isFaceMatch(validation: IValidation): validation is IFaceMatchValidation {
	return validation.type === ValidationType.FaceMatch && validation.hasOwnProperty('imageToCompare');
}

/* ---------- DocumentVerification Validation ---------- */
export type IDocumentVerification = IValidation & {
	type: typeof ValidationType.DocumentVerification;
	documentType: ValidationDocType[] | null;
	countryCodes: ValidationDocCountryCode | null;
	valueToCompareSource: IDocumentVerificationValueSource | null;
};

export function isDocumentVerification(validation: IValidation): validation is IDocumentVerification {
	return validation.type === ValidationType.DocumentVerification && validation.hasOwnProperty('documentType');
}

/* -------------------------------------------------------------------------------------------------------------- */
